// Drawer — 1:1 port of legacy `src/ui/Drawer.jsx` plus Phase-4 a11y
// additions.md: initial focus → first focusable, Tab-trap,
// focus-return to original trigger on unmount. Legacy contract
// preserved: no `open` prop — caller unmounts to close.
import { useEffect, useRef, useState, type ReactNode } from 'react';

const FOCUSABLE_SELECTOR =
  'input, select, textarea, [href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DrawerProps {
  children: ReactNode;
  onClose?: () => void;
  width?: number | string;
  narrow?: boolean;
  /**
   * When true, Escape and backdrop click do not invoke `onClose`.
   * Mirrors `Modal.dismissDisabled` so unsaved-changes guards can suppress
   * dismissal while a warning prompt is open or a mutation is in flight.
   */
  dismissDisabled?: boolean;
}

export function Drawer({ children, onClose, width, narrow, dismissDisabled = false }: DrawerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<Element | null>(null);
  const onCloseRef = useRef(onClose);
  const dismissDisabledRef = useRef(dismissDisabled);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    dismissDisabledRef.current = dismissDisabled;
  }, [dismissDisabled]);
  const [mounted, setMounted] = useState(false);

  function attemptDismiss(): void {
    if (dismissDisabledRef.current) return;
    onCloseRef.current?.();
  }

  // Animate-in via rAF (legacy behavior).
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Escape → onClose (legacy behavior, suppressed by dismissDisabled).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') attemptDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus management.md.
  useEffect(() => {
    // Capture trigger for focus-return.
    triggerRef.current = document.activeElement instanceof Element ? document.activeElement : null;

    const raf = requestAnimationFrame(() => {
      const root = rootRef.current;
      if (!root) return;
      const first = root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (first) first.focus();
      else root.focus();
    });

    return () => {
      cancelAnimationFrame(raf);
      const t = triggerRef.current;
      if (t && t instanceof HTMLElement && t.isConnected) t.focus();
    };
  }, []);

  // Tab-trap inside the drawer.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled'),
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    root.addEventListener('keydown', onKey);
    return () => root.removeEventListener('keydown', onKey);
  }, []);

  const panelWidth = narrow
    ? '100%'
    : width != null
      ? typeof width === 'number'
        ? `min(${width}px, calc(100vw - 180px))`
        : width
      : 'min(880px, calc(100vw - 180px))';

  return (
    // Legacy-faithful backdrop: plain <div> whose click dismisses.
    // Escape-to-close is bound above at window level, so keyboard users are
    // covered; this div is purely a pointer-dismiss affordance.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={attemptDismiss}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        display: 'flex',
        justifyContent: 'flex-end',
        background: mounted ? 'var(--scrim)' : 'transparent',
        transition: 'background var(--dur-2) var(--ease-out)',
      }}
    >
      {/* Panel stops click propagation so inner content is not accidentally dismissed. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        ref={rootRef}
        data-drawer
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: panelWidth,
          maxWidth: '100%',
          height: '100%',
          background: 'var(--bg-1)',
          boxShadow: narrow ? 'none' : 'var(--shadow-3)',
          borderLeft: narrow ? 'none' : '1px solid var(--border-1)',
          transform: mounted ? 'translateX(0)' : 'translateX(24px)',
          opacity: mounted ? 1 : 0,
          transition:
            'transform var(--dur-2) var(--ease-out), opacity var(--dur-2) var(--ease-out)',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          outline: 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
